'use client';

import type { FC } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { useComputedColorScheme, useMantineColorScheme } from '@mantine/core';
import { FiMoon, FiSun } from 'react-icons/fi';

import { Switch } from '@highlight-cp/core-ui';

import { TEST_ID } from '../constants/testIds';

/**
 * Light/dark color-scheme switch for the top nav (Mantine "icon labels"
 * pattern): a sun on the off/light side, a moon on the on/dark side.
 * `getInitialValueInEffect` keeps the SSR render (light) from mismatching.
 */
const ColorSchemeToggle: FC = () => {
  const { t } = useTranslation();
  const { setColorScheme } = useMantineColorScheme();
  const computed = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDark = computed === 'dark';

  return (
    <Switch
      size="md"
      checked={isDark}
      onChange={() => setColorScheme(isDark ? 'light' : 'dark')}
      onLabel={<FiMoon size={14} />}
      offLabel={<FiSun size={14} />}
      aria-label={t('Dark mode')}
      data-testid={TEST_ID.TOP_NAV_COLOR_SCHEME_TOGGLE}
    />
  );
};

export default ColorSchemeToggle;
